import { LoginRequest, LoginResponse } from '@codey2/codey-api-types/models';
import React, { createContext, useState, ReactNode, useEffect, useContext } from 'react';
import { codeyApiClient } from '../api/codeyApiClient';

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const checkExistingSession = () => {
      try {
        const savedUser = sessionStorage.getItem(SESSION_STORAGE_KEY);
        if (savedUser) {
          const user: User = JSON.parse(savedUser);
          setAuthState({
            user,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setAuthState(prev => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Error reading session storage:', error);
        setAuthState(prev => ({ ...prev, isLoading: false }));
      }
    };

    checkExistingSession();
  }, []);

  const login = async (credentials: LoginRequest): Promise<{ success: boolean; error?: string }> => {
    try {
      const user = await codeyApiClient.post<LoginRequest, LoginResponse>('/auth/login', credentials);
      sessionStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(user));

      setAuthState({
        user,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch (error) {
      return { success: false, error: error instanceof Error ? error.message : 'Login mislukt' };
    }
    return { success: true };
  };

  const logout = () => {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
    setAuthState({
      user: null,
      isAuthenticated: false,
      isLoading: false,
    });
  };

  const contextValue: AuthContextValue = {
    ...authState,
    login,
    logout,
  };

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export interface AuthContextValue extends AuthState {
  login: (credentials: LoginRequest) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

export interface User {
  username: string;
  role: 'STUDENT' | 'TEACHER' | 'ADMIN';
  token: string;
}

const SESSION_STORAGE_KEY = 'codey_user';
